import React from "react";
import { useTranslation } from "react-i18next";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermissions, type PermissionAction } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

export { AccessDenied } from "./AccessDenied";

interface PermissionGuardProps {
  section: PLATFORM_SECTION_ENUM;
  action: PermissionAction;
  mode?: "hide" | "disable";
  /** Overrides the default "no permission" message. */
  tooltip?: string;
  children: React.ReactElement<any>;
}

export function PermissionGuard({
  section,
  action,
  mode = "disable",
  tooltip,
  children,
}: PermissionGuardProps) {
  const { t } = useTranslation(["common"]);
  const { can, isLoading } = usePermissions();
  const allowed = can(section, action);
  const tooltipText =
    tooltip ?? t("common:access.no_permission_to_perform_action");

  if (isLoading) {
    return mode === "hide"
      ? null
      : React.cloneElement(children, { disabled: true });
  }

  if (allowed) return children;
  if (mode === "hide") return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            {React.cloneElement(children, { disabled: true })}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
